/**
 * Effects table — renders the `step.effects` payload as a readable table.
 *
 * Spec: dev/projects/sl2-sstv2-live-trace.md §4.3 (Effects section)
 *
 * The backend `effects` field is loosely typed (per types.ts), but we
 * normalize it here into a flat list of typed rows so the rendering is
 * deterministic and unit-testable.
 */
import {
  Box,
  Chip,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { Link as RouterLink } from "react-router-dom"

export type EffectRow =
  | {
      type: "ddb-write"
      thingTypeMrn: string
      thingId: string
      op: "create" | "update" | "delete"
    }
  | {
      type: "sqs"
      queueName: string
      messageId?: string
    }
  | {
      type: "iot"
      topic: string
    }
  | {
      type: "partner-api"
      method: string
      url: string
      status?: number
      latencyMs?: number
    }
  | {
      type: "lambda"
      functionName: string
      invocationType: string
    }

/** Convert the loosely-typed effects shape into a typed row list. */
export const normalizeEffects = (effects: unknown): EffectRow[] => {
  if (!effects || typeof effects !== "object") return []
  const e = effects as Record<string, unknown>
  const out: EffectRow[] = []
  if (Array.isArray(e.ddbWrites)) {
    for (const w of e.ddbWrites) {
      if (w && typeof w === "object") {
        const ww = w as Record<string, unknown>
        if (
          typeof ww.thingTypeMrn === "string" &&
          typeof ww.thingId === "string" &&
          (ww.op === "create" || ww.op === "update" || ww.op === "delete")
        ) {
          out.push({
            type: "ddb-write",
            thingTypeMrn: ww.thingTypeMrn,
            thingId: ww.thingId,
            op: ww.op,
          })
        }
      }
    }
  }
  if (Array.isArray(e.sqsMessages)) {
    for (const s of e.sqsMessages) {
      if (s && typeof s === "object") {
        const ss = s as Record<string, unknown>
        if (typeof ss.queueName === "string") {
          out.push({
            type: "sqs",
            queueName: ss.queueName,
            messageId:
              typeof ss.messageId === "string" ? ss.messageId : undefined,
          })
        }
      }
    }
  }
  if (Array.isArray(e.iotMessages)) {
    for (const m of e.iotMessages) {
      if (m && typeof m === "object") {
        const mm = m as Record<string, unknown>
        if (typeof mm.topic === "string") {
          out.push({ type: "iot", topic: mm.topic })
        }
      }
    }
  }
  if (Array.isArray(e.partnerApiCalls)) {
    for (const p of e.partnerApiCalls) {
      if (p && typeof p === "object") {
        const pp = p as Record<string, unknown>
        if (typeof pp.method === "string" && typeof pp.url === "string") {
          out.push({
            type: "partner-api",
            method: pp.method,
            url: pp.url,
            status: typeof pp.status === "number" ? pp.status : undefined,
            latencyMs:
              typeof pp.latencyMs === "number" ? pp.latencyMs : undefined,
          })
        }
      }
    }
  }
  if (Array.isArray(e.lambdaInvokes)) {
    for (const l of e.lambdaInvokes) {
      if (l && typeof l === "object") {
        const ll = l as Record<string, unknown>
        if (
          typeof ll.functionName === "string" &&
          typeof ll.invocationType === "string"
        ) {
          out.push({
            type: "lambda",
            functionName: ll.functionName,
            invocationType: ll.invocationType,
          })
        }
      }
    }
  }
  return out
}

const ApiStatusChip: FC<{ status?: number }> = ({ status }) => {
  if (status == null) return <span>-</span>
  const color = status >= 500 ? "error" : status >= 400 ? "warning" : "success"
  return <Chip size="small" label={String(status)} color={color} />
}

export const Sstv2TraceEffectsTable: FC<{ effects: unknown }> = ({ effects }) => {
  const rows = normalizeEffects(effects)
  if (rows.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No effects.
      </Typography>
    )
  }
  return (
    <Box sx={{ overflow: "auto" }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Detail</TableCell>
            <TableCell>Extra</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => {
            switch (row.type) {
              case "ddb-write":
                return (
                  <TableRow key={`ddb-${idx}`}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={`DDB ${row.op}`}
                        color={
                          row.op === "create"
                            ? "success"
                            : row.op === "delete"
                              ? "error"
                              : "primary"
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/sa/things/${encodeURIComponent(
                          row.thingTypeMrn,
                        )}/${encodeURIComponent(row.thingId)}`}
                        sx={{ fontFamily: "monospace", fontSize: 12 }}
                      >
                        {row.thingId}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        fontFamily="monospace"
                        color="text.secondary"
                      >
                        {row.thingTypeMrn}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              case "sqs":
                return (
                  <TableRow key={`sqs-${idx}`}>
                    <TableCell>
                      <Chip size="small" label="SQS" color="secondary" />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {row.queueName}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="caption"
                        fontFamily="monospace"
                        color="text.secondary"
                      >
                        {row.messageId ?? "-"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
              case "iot":
                return (
                  <TableRow key={`iot-${idx}`}>
                    <TableCell>
                      <Chip size="small" label="IoT" color="info" />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {row.topic}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                )
              case "partner-api":
                return (
                  <TableRow key={`api-${idx}`}>
                    <TableCell>
                      <Chip size="small" label={row.method} color="warning" />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {row.url}
                    </TableCell>
                    <TableCell>
                      <ApiStatusChip status={row.status} />
                      {row.latencyMs != null && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 1 }}
                        >
                          {row.latencyMs} ms
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )
              case "lambda":
                return (
                  <TableRow key={`lambda-${idx}`}>
                    <TableCell>
                      <Chip size="small" label="Lambda" />
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {row.functionName}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {row.invocationType}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )
            }
          })}
        </TableBody>
      </Table>
    </Box>
  )
}

export default Sstv2TraceEffectsTable
